import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocation } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { Empresa, RemontaDossieSinistro, Veiculo } from "@shared/schema";
import { api } from "@shared/routes";
import { ACTIVE_SERVICE_TYPES, type ServiceType } from "@shared/pre-analise/types";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { vehiclesForRequester } from "@/lib/dossier-web";
import { cn } from "@/lib/utils";
import { ArrowLeft, ArrowRight, Check, Loader2, Save, Send, Trash2 } from "lucide-react";
import {
  buildDossierPayload, CANAIS_ENTREGA, DOSSIER_FIELD_LABELS, DOSSIER_STEPS, draftStorageKey, emptyDossierForm, ENTIDADE_TIPOS,
  EVENTO_NATUREZAS, firstInvalidStep, REGULACAO_STATUS, restoreDossierDraft, serializeDossierDraft, SERVICE_LABELS,
  UFS, validateDossierStep, type DossierFormErrors, type DossierFormState,
} from "./dossie-form";

type Field = keyof DossierFormState;

const MONTA_LABELS: Record<string, string> = { pequena_monta: "Pequena monta", media_monta: "Média monta", grande_monta: "Grande monta" };
const OBJETO_LABELS: Record<DossierFormState["objetoTipo"], string> = { veiculo: "Veículo", imovel: "Imóvel ou estrutura", outro: "Outro bem" };


/** Campos exibidos na revisão, por etapa. */
const REVIEW_FIELDS: Field[][] = [
  ["solicitanteId", "clienteEntidade", "entidadeTipo", "entidadeCnpj", "unidadeFilial", "profissionalSolicitante", "solicitanteSetor", "solicitanteEmail", "solicitanteTelefone", "solicitadoEm", "prazoSolicitado"],
  ["numeroSinistro", "numeroApolice", "seguradoNome", "seguradoDocumento", "vigenciaInicio", "vigenciaFim", "coberturaAcionada", "franquiaParticipacao", "regulacaoStatus", "valorReclamado"],
  ["dataSinistro", "eventoHorario", "eventoMunicipio", "eventoUf", "eventoEndereco", "eventoLocalizacao", "eventoOrgaoRegistro", "eventoNaturezas", "descricaoEvento"],
  ["objetoTipo", "veiculoId", "classificacaoOriginal", "ufRegistro", "numeroBat", "valorFipe", "mesReferenciaFipe", "veiculosEnvolvidos", "bensTerceirosEnvolvidos"],
  ["primeiroServico", "numeroOrdemExterna", "reguladorExterno", "vistoriaPrevistaEm", "prazoEntregaEm"],
  ["contatoAcessoNome", "contatoAcessoTelefone", "canalEntrega", "documentosIniciaisReferencia", "responsavelInformacoesNome", "responsavelInformacoesCargo", "autorizadoEnvio"],
];
const VEHICLE_ONLY: Field[] = ["veiculoId", "classificacaoOriginal", "ufRegistro", "numeroBat", "valorFipe", "mesReferenciaFipe"];

const fieldId = (field: Field) => `dossie-${field}`;
const errorId = (field: Field) => `dossie-${field}-erro`;
const money = (value: string) => (value ? Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "");
const date = (value: string) => (/^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T12:00:00`).toLocaleDateString("pt-BR") : value);

async function json<T>(method: string, path: string, body?: unknown): Promise<T> {
  return (await apiRequest(method, path, body)).json();
}

/** Lê/grava o rascunho local; storage indisponível (modo privado, cota) vira erro explícito, nunca sucesso falso. */
function draftStore(key: string | null) {
  return {
    read(): string | null {
      if (!key) return null;
      try { return window.localStorage.getItem(key); } catch { return null; }
    },
    write(value: string): boolean {
      if (!key) return false;
      try { window.localStorage.setItem(key, value); return true; } catch { return false; }
    },
    clear() {
      if (!key) return;
      try { window.localStorage.removeItem(key); } catch { /* nada a limpar */ }
    },
  };
}

function FieldShell({ field, label, required, error, hint, children, className }: {
  field: Field; label: string; required?: boolean; error?: string; hint?: string; children: ReactNode; className?: string;
}) {
  return (
    <div className={cn("min-w-0 space-y-1.5", className)}>
      <Label htmlFor={fieldId(field)} className="text-slate-800">
        {label}{required && <span aria-hidden="true" className="ml-0.5 text-destructive">*</span>}
        {required && <span className="sr-only"> (obrigatório)</span>}
      </Label>
      {children}
      {hint && !error && <p className="text-xs text-muted-foreground">{hint}</p>}
      {error && <p id={errorId(field)} className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

export function DossieAberturaWizard() {
  const [, navigate] = useLocation();
  const { profile } = useAuth();
  const { toast } = useToast();
  const inspector = profile?.empresa.tipo === "VISTORIADORA";
  const store = useMemo(() => draftStore(profile ? draftStorageKey(profile.id, profile.empresaId) : null), [profile]);

  const [state, setState] = useState<DossierFormState>(emptyDossierForm);
  const [step, setStep] = useState(0);
  const [maxStep, setMaxStep] = useState(0);
  const [errors, setErrors] = useState<DossierFormErrors>({});
  const [draftInfo, setDraftInfo] = useState<string | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const submitting = useRef(false);
  const loadedFor = useRef<string | null>(null);

  // Carrega o rascunho do usuário/empresa atual; sem rascunho, preenche o que já se sabe do perfil.
  useEffect(() => {
    if (!profile || loadedFor.current === `${profile.empresaId}:${profile.id}`) return;
    loadedFor.current = `${profile.empresaId}:${profile.id}`;
    const raw = store.read();
    const draft = restoreDossierDraft(raw);
    if (draft) {
      setState(draft);
      const savedAt = (() => { try { return JSON.parse(raw!).savedAt as string; } catch { return null; } })();
      setDraftInfo(savedAt ? new Date(savedAt).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "data desconhecida");
      return;
    }
    if (raw) store.clear(); // rascunho corrompido ou de versão antiga
    setState({
      ...emptyDossierForm(),
      solicitanteId: inspector ? "" : profile.empresaId,
      clienteEntidade: inspector ? "" : profile.empresa.razaoSocial ?? "",
      profissionalSolicitante: profile.nome ?? "",
      solicitanteEmail: profile.email ?? "",
      solicitanteTelefone: profile.telefone ?? "",
      solicitadoEm: new Date().toLocaleDateString("en-CA"),
      responsavelInformacoesNome: profile.nome ?? "",
    });
  }, [profile, store, inspector]);

  const { data: requesters = [] } = useQuery<Empresa[]>({
    queryKey: [api.remontaPermissaoVistoriadora.listSolicitantesAutorizados.path],
    queryFn: () => json("GET", api.remontaPermissaoVistoriadora.listSolicitantesAutorizados.path),
    enabled: inspector,
  });
  const { data: vehicles = [] } = useQuery<Veiculo[]>({ queryKey: [api.veiculos.list.path], queryFn: () => json("GET", api.veiculos.list.path) });
  const availableVehicles = vehiclesForRequester(vehicles, state.solicitanteId);

  const set = <K extends Field>(field: K, value: DossierFormState[K]) => {
    // Trocar o Solicitante invalida o veículo escolhido (veículos pertencem ao Solicitante).
    setState((old) => ({ ...old, [field]: value, ...(field === "solicitanteId" && value !== old.solicitanteId ? { veiculoId: "" } : {}) }));
    if (errors[field]) setErrors((old) => ({ ...old, [field]: undefined }));
  };

  const goTo = (target: number) => {
    setErrors({});
    setStep(target);
    setMaxStep((old) => Math.max(old, target));
    requestAnimationFrame(() => headingRef.current?.focus());
  };

  const showErrors = (target: number, found: DossierFormErrors) => {
    setStep(target);
    setErrors(found);
    const first = Object.keys(found)[0] as Field | undefined;
    requestAnimationFrame(() => {
      const element = first && (document.getElementById(fieldId(first)) ?? document.querySelector<HTMLElement>(`[data-field="${first}"]`));
      (element ?? headingRef.current)?.focus();
    });
  };

  const next = () => {
    const found = validateDossierStep(state, step, { inspector });
    if (Object.keys(found).length) return showErrors(step, found);
    goTo(step + 1);
  };

  const saveDraft = () => {
    if (store.write(serializeDossierDraft(state))) {
      setDraftInfo(new Date().toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }));
      toast({ title: "Rascunho salvo neste navegador", description: "Volte a esta tela para continuar de onde parou." });
    } else {
      toast({ title: "Não foi possível salvar o rascunho", description: "O navegador bloqueou o armazenamento local. Envie o Dossiê ou mantenha esta aba aberta.", variant: "destructive" });
    }
  };

  const discardDraft = () => {
    store.clear();
    setDraftInfo(null);
    loadedFor.current = null;
    setState(emptyDossierForm());
    goTo(0);
  };

  const submit = useMutation({
    mutationFn: () => json<RemontaDossieSinistro>("POST", api.remontaDossies.create.path, buildDossierPayload(state, { inspector })),
    onSuccess: (dossier) => {
      store.clear();
      queryClient.invalidateQueries({ queryKey: [api.remontaDossies.list.path] });
      toast({ title: "Dossiê enviado para pré-análise", description: "Os dados serão estudados antes da contratação de um serviço." });
      navigate(`/dossies/${dossier.id}`);
    },
    onError: (error: Error) => {
      const message = error.message.replace(/^\d+:\s*/, "");
      let detail = message;
      try { detail = JSON.parse(message).message ?? message; } catch { /* texto simples */ }
      toast({ title: "Não foi possível abrir o Dossiê", description: `${detail} Seus dados continuam nesta tela.`, variant: "destructive" });
    },
    onSettled: () => { submitting.current = false; },
  });

  const send = () => {
    if (submitting.current || submit.isPending) return; // guarda síncrona contra duplo clique
    const invalid = firstInvalidStep(state, { inspector });
    if (invalid !== null) return showErrors(invalid, validateDossierStep(state, invalid, { inspector }));
    submitting.current = true;
    submit.mutate();
  };

  const a11y = (field: Field) => ({
    id: fieldId(field),
    "aria-invalid": errors[field] ? true : undefined,
    "aria-describedby": errors[field] ? errorId(field) : undefined,
  });
  const text = (field: Field, options: { required?: boolean; type?: string; hint?: string; className?: string; inputMode?: "numeric" | "tel" | "email"; maxLength?: number } = {}) => (
    <FieldShell field={field} label={DOSSIER_FIELD_LABELS[field as Exclude<Field, "objetoTipo">]} required={options.required} error={errors[field]} hint={options.hint} className={options.className}>
      <Input {...a11y(field)} type={options.type ?? "text"} inputMode={options.inputMode} maxLength={options.maxLength}
        value={state[field] as string} onChange={(event) => set(field, event.target.value as never)} />
    </FieldShell>
  );
  const area = (field: Field, options: { required?: boolean; hint?: string } = {}) => (
    <FieldShell field={field} label={DOSSIER_FIELD_LABELS[field as Exclude<Field, "objetoTipo">]} required={options.required} error={errors[field]} hint={options.hint} className="sm:col-span-2">
      <Textarea {...a11y(field)} rows={4} value={state[field] as string} onChange={(event) => set(field, event.target.value as never)} />
    </FieldShell>
  );
  const choice = (field: Field, options: { value: string; label: string }[], config: { required?: boolean; placeholder?: string; disabled?: boolean; className?: string } = {}) => (
    <FieldShell field={field} label={DOSSIER_FIELD_LABELS[field as Exclude<Field, "objetoTipo">]} required={config.required} error={errors[field]} className={config.className}>
      <Select value={(state[field] as string) || undefined} onValueChange={(value) => set(field, value as never)} disabled={config.disabled}>
        <SelectTrigger {...a11y(field)}><SelectValue placeholder={config.placeholder ?? "Selecione"} /></SelectTrigger>
        <SelectContent>{options.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
      </Select>
    </FieldShell>
  );
  const moneyField = (field: Field, options: { required?: boolean } = {}) => (
    <FieldShell field={field} label={DOSSIER_FIELD_LABELS[field as Exclude<Field, "objetoTipo">]} required={options.required} error={errors[field]}>
      <CurrencyInput {...a11y(field)} value={state[field] as string} onChange={(value) => set(field, value as never)} />
    </FieldShell>
  );
  const list = (values: readonly string[]) => values.map((value) => ({ value, label: value }));

  const requesterName = (id: string) => {
    const company = requesters.find((item) => item.id === id);
    return company ? company.nomeFantasia || company.razaoSocial : profile?.empresaId === id ? profile.empresa.nomeFantasia || profile.empresa.razaoSocial : id;
  };
  const vehicleName = (id: string) => {
    const vehicle = vehicles.find((item) => item.id === id);
    return vehicle ? `${vehicle.placa || "Sem placa"} · ${vehicle.marca} ${vehicle.modelo}` : id;
  };
  const display = (field: Field): string => {
    const value = state[field];
    if (field === "objetoTipo") return OBJETO_LABELS[state.objetoTipo];
    if (field === "autorizadoEnvio") return value ? "Confirmada" : "";
    if (Array.isArray(value)) return value.join(", ");
    if (!value) return "";
    if (field === "solicitanteId") return requesterName(value as string);
    if (field === "veiculoId") return vehicleName(value as string);
    if (field === "classificacaoOriginal") return MONTA_LABELS[value as string] ?? (value as string);
    if (field === "primeiroServico") return SERVICE_LABELS[value as ServiceType] ?? (value as string);
    if (["franquiaParticipacao", "valorReclamado", "valorFipe"].includes(field)) return money(value as string);
    if (field === "mesReferenciaFipe") return (value as string).split("-").reverse().join("/");
    return date(value as string);
  };

  const current = DOSSIER_STEPS[step];
  const progress = Math.round(((step + 1) / DOSSIER_STEPS.length) * 100);

  return (
    <div className="min-w-0 space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Button variant="ghost" size="sm" className="-ml-2 mb-1 text-muted-foreground" onClick={() => navigate("/dossies")}>
            <ArrowLeft className="mr-1 h-4 w-4" />Dossiês
          </Button>
          <h1 className="text-2xl font-bold text-slate-900">Novo Dossiê do Sinistro</h1>
          <p className="text-sm text-muted-foreground">Abertura com a primeira Ordem de serviço. Campos com * são obrigatórios.</p>
        </div>
      </div>

      {draftInfo && (
        <div role="status" className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          <span>Rascunho salvo em {draftInfo} restaurado.</span>
          <Button variant="ghost" size="sm" className="text-blue-900 hover:bg-blue-100" onClick={discardDraft}>
            <Trash2 className="mr-1 h-4 w-4" />Descartar rascunho
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[15rem_minmax(0,1fr)]">
        <nav aria-label="Etapas da abertura" className="hidden lg:block">
          <ol className="space-y-1">
            {DOSSIER_STEPS.map((item, index) => {
              const done = index < step || (index <= maxStep && index !== step);
              return (
                <li key={item.id}>
                  <button type="button" disabled={index > maxStep} onClick={() => goTo(index)}
                    aria-current={index === step ? "step" : undefined}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors",
                      index === step ? "bg-primary/10 font-semibold text-primary" : "text-slate-700 hover:bg-slate-100",
                      "disabled:cursor-not-allowed disabled:text-slate-400 disabled:hover:bg-transparent",
                    )}>
                    <span className={cn(
                      "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs tabular-nums",
                      index === step ? "border-primary bg-primary text-primary-foreground" : done ? "border-primary/40 text-primary" : "border-slate-300",
                    )}>
                      {done && index !== step ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : index + 1}
                    </span>
                    {item.title}
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>

        <section aria-labelledby="dossie-etapa-titulo" className="min-w-0 rounded-md border bg-white">
          <div className="border-b px-4 py-4 sm:px-6">
            <p className="text-xs font-medium tabular-nums text-muted-foreground">Etapa {step + 1} de {DOSSIER_STEPS.length}</p>
            <h2 id="dossie-etapa-titulo" ref={headingRef} tabIndex={-1} className="mt-0.5 text-lg font-semibold text-slate-900 outline-none">{current.title}</h2>
            <Progress value={progress} className="mt-3 h-1.5 lg:hidden" aria-label={`Progresso: etapa ${step + 1} de ${DOSSIER_STEPS.length}`} />
          </div>

          <div className="grid grid-cols-1 gap-x-5 gap-y-4 px-4 py-5 sm:grid-cols-2 sm:px-6">
            {current.id === "cliente" && <>
              {inspector && choice("solicitanteId", requesters.map((item) => ({ value: item.id, label: item.nomeFantasia || item.razaoSocial })),
                { required: true, placeholder: "Selecione um Solicitante autorizado", className: "sm:col-span-2" })}
              {text("clienteEntidade", { required: true, className: "sm:col-span-2" })}
              {choice("entidadeTipo", list(ENTIDADE_TIPOS), { required: true })}
              {text("entidadeCnpj", { inputMode: "numeric" })}
              {text("unidadeFilial")}
              {text("profissionalSolicitante", { required: true })}
              {text("solicitanteSetor", { required: true })}
              {text("solicitanteEmail", { required: true, type: "email", inputMode: "email" })}
              {text("solicitanteTelefone", { required: true, type: "tel", inputMode: "tel" })}
              {text("solicitadoEm", { required: true, type: "date" })}
              {text("prazoSolicitado", { type: "date" })}
            </>}

            {current.id === "sinistro" && <>
              {text("numeroSinistro", { required: true })}
              {text("numeroApolice")}
              {text("seguradoNome", { required: true })}
              {text("seguradoDocumento", { inputMode: "numeric" })}
              {text("vigenciaInicio", { type: "date" })}
              {text("vigenciaFim", { type: "date" })}
              {text("coberturaAcionada")}
              {choice("regulacaoStatus", list(REGULACAO_STATUS))}
              {moneyField("franquiaParticipacao")}
              {moneyField("valorReclamado")}
            </>}

            {current.id === "evento" && <>
              {text("dataSinistro", { required: true, type: "date" })}
              {text("eventoHorario", { type: "time" })}
              {text("eventoMunicipio", { required: true })}
              {choice("eventoUf", list(UFS), { required: true })}
              {text("eventoEndereco", { required: true, className: "sm:col-span-2" })}
              {text("eventoLocalizacao", { hint: "Ex.: -19.9167, -43.9345" })}
              {text("eventoOrgaoRegistro", { hint: "Ex.: Polícia Militar, Defesa Civil" })}
              <fieldset className="min-w-0 sm:col-span-2" aria-describedby={errors.eventoNaturezas ? errorId("eventoNaturezas") : undefined}>
                <legend className="mb-2 text-sm font-medium text-slate-800">
                  Natureza do evento<span aria-hidden="true" className="ml-0.5 text-destructive">*</span><span className="sr-only"> (obrigatório)</span>
                </legend>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
                  {EVENTO_NATUREZAS.map((natureza, index) => {
                    const id = `dossie-natureza-${index}`;
                    const checked = state.eventoNaturezas.includes(natureza);
                    return (
                      <label key={natureza} htmlFor={id} className="flex min-h-10 cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-slate-50">
                        <Checkbox id={id} data-field={index === 0 ? "eventoNaturezas" : undefined} checked={checked}
                          aria-invalid={errors.eventoNaturezas ? true : undefined}
                          onCheckedChange={(value) => set("eventoNaturezas", value ? [...state.eventoNaturezas, natureza] : state.eventoNaturezas.filter((item) => item !== natureza))} />
                        {natureza}
                      </label>
                    );
                  })}
                </div>
                {errors.eventoNaturezas && <p id={errorId("eventoNaturezas")} className="mt-1.5 text-sm text-destructive">{errors.eventoNaturezas}</p>}
              </fieldset>
              {area("descricaoEvento", { required: true })}
            </>}

            {current.id === "envolvidos" && <>
              <fieldset className="min-w-0 sm:col-span-2">
                <legend className="mb-2 text-sm font-medium text-slate-800">Objeto principal do sinistro</legend>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3" role="radiogroup">
                  {(Object.keys(OBJETO_LABELS) as DossierFormState["objetoTipo"][]).map((objeto) => (
                    <label key={objeto} className={cn("flex min-h-10 cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm",
                      state.objetoTipo === objeto ? "border-primary bg-primary/5 text-slate-900" : "hover:bg-slate-50")}>
                      <input type="radio" name="dossie-objeto" value={objeto} checked={state.objetoTipo === objeto}
                        onChange={() => set("objetoTipo", objeto)} className="h-4 w-4 accent-[hsl(var(--primary))]" />
                      {OBJETO_LABELS[objeto]}
                    </label>
                  ))}
                </div>
              </fieldset>
              {state.objetoTipo === "veiculo" && <>
                {choice("veiculoId", availableVehicles.map((vehicle) => ({ value: vehicle.id, label: `${vehicle.placa || "Sem placa"} · ${vehicle.marca} ${vehicle.modelo}` })), {
                  required: true, className: "sm:col-span-2", disabled: !state.solicitanteId,
                  placeholder: state.solicitanteId ? (availableVehicles.length ? "Selecione o veículo" : "Nenhum veículo cadastrado para este Solicitante") : "Escolha o Solicitante na etapa 1",
                })}
                {choice("classificacaoOriginal", Object.entries(MONTA_LABELS).map(([value, label]) => ({ value, label })), { required: true })}
                {choice("ufRegistro", list(UFS), { required: true })}
                {text("numeroBat")}
                <div className="hidden sm:block" />
                {moneyField("valorFipe")}
                {text("mesReferenciaFipe", { type: "month", hint: "Obrigatória quando houver valor FIPE" })}
              </>}
              {area("veiculosEnvolvidos")}
              {area("bensTerceirosEnvolvidos")}
            </>}

            {current.id === "servico" && <>
              <p className="text-sm text-muted-foreground sm:col-span-2">A pré-análise estuda o sinistro e indica os serviços tecnicamente viáveis. Se já houver uma preferência, informe abaixo. Nenhuma Ordem será aberta neste envio.</p>
              {choice("primeiroServico", ACTIVE_SERVICE_TYPES.map((service) => ({
                value: service, label: SERVICE_LABELS[service] + (service === "reclassificacao_monta" && state.objetoTipo !== "veiculo" ? " (exige veículo)" : ""),
              })), { className: "sm:col-span-2" })}
              {text("numeroOrdemExterna")}
              {text("reguladorExterno")}
              {text("vistoriaPrevistaEm", { type: "date" })}
              {text("prazoEntregaEm", { type: "date", hint: "Sem prazo, vale o prazo solicitado" })}
            </>}

            {current.id === "contatos" && <>
              {text("contatoAcessoNome")}
              {text("contatoAcessoTelefone", { type: "tel", inputMode: "tel" })}
              {choice("canalEntrega", list(CANAIS_ENTREGA))}
              {text("documentosIniciaisReferencia")}
              <h3 className="pt-2 text-sm font-semibold text-slate-900 sm:col-span-2">Responsável pelas informações</h3>
              {text("responsavelInformacoesNome", { required: true })}
              {text("responsavelInformacoesCargo", { required: true })}
              <div className="min-w-0 sm:col-span-2">
                <label htmlFor={fieldId("autorizadoEnvio")} className={cn("flex cursor-pointer items-start gap-3 rounded-md border px-3 py-3 text-sm",
                  errors.autorizadoEnvio ? "border-destructive" : "hover:bg-slate-50")}>
                  <Checkbox id={fieldId("autorizadoEnvio")} className="mt-0.5" checked={state.autorizadoEnvio}
                    aria-invalid={errors.autorizadoEnvio ? true : undefined}
                    aria-describedby={errors.autorizadoEnvio ? errorId("autorizadoEnvio") : undefined}
                    onCheckedChange={(value) => set("autorizadoEnvio", value === true)} />
                  <span>Declaro que estou autorizado(a) a encaminhar os dados e documentos deste sinistro e respondo pelas informações enviadas.<span aria-hidden="true" className="ml-0.5 text-destructive">*</span></span>
                </label>
                {errors.autorizadoEnvio && <p id={errorId("autorizadoEnvio")} className="mt-1.5 text-sm text-destructive">{errors.autorizadoEnvio}</p>}
              </div>
            </>}

            {current.id === "revisao" && (
              <div className="space-y-6 sm:col-span-2">
                {REVIEW_FIELDS.map((fields, index) => (
                  <section key={DOSSIER_STEPS[index].id} aria-labelledby={`revisao-${index}`} className="min-w-0">
                    <div className="mb-2 flex items-center justify-between gap-2 border-b pb-1.5">
                      <h3 id={`revisao-${index}`} className="text-sm font-semibold text-slate-900">{DOSSIER_STEPS[index].title}</h3>
                      <Button variant="ghost" size="sm" className="h-8 px-2 text-primary" onClick={() => goTo(index)}>Editar<span className="sr-only"> {DOSSIER_STEPS[index].title}</span></Button>
                    </div>
                    <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                      {fields.filter((field) => (field !== "solicitanteId" || inspector) && (state.objetoTipo === "veiculo" || !VEHICLE_ONLY.includes(field))).map((field) => {
                        const value = display(field);
                        return (
                          <div key={field} className={cn("min-w-0", ["descricaoEvento", "veiculosEnvolvidos", "bensTerceirosEnvolvidos"].includes(field) && "sm:col-span-2")}>
                            <dt className="text-xs text-muted-foreground">{field === "objetoTipo" ? "Objeto principal" : DOSSIER_FIELD_LABELS[field as Exclude<Field, "objetoTipo">]}</dt>
                            <dd className={cn("whitespace-pre-line [overflow-wrap:anywhere]", value ? "text-slate-900" : "text-slate-500")}>{value || "Não informado"}</dd>
                          </div>
                        );
                      })}
                    </dl>
                  </section>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col-reverse gap-2 border-t px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              {step > 0 && <Button variant="outline" onClick={() => goTo(step - 1)} disabled={submit.isPending}><ArrowLeft className="mr-2 h-4 w-4" />Voltar</Button>}
              <Button variant="ghost" onClick={saveDraft} disabled={submit.isPending}><Save className="mr-2 h-4 w-4" />Salvar rascunho</Button>
            </div>
            {current.id === "revisao" ? (
              <Button onClick={send} disabled={submit.isPending} data-testid="button-enviar-dossie">
                {submit.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                {submit.isPending ? "Enviando..." : "Abrir Dossiê e solicitar serviço"}
              </Button>
            ) : (
              <Button onClick={next}>Continuar<ArrowRight className="ml-2 h-4 w-4" /></Button>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
